import React, { useCallback, useContext } from "react";
import { Form } from "react-final-form";
import { Box, Button, TableHead, Typography } from "@mui/material";
import { Feature } from "geojson";

import { TableBody, TableCell, TableRow } from "ui";

import { SelectField, SwitchField, TextField } from "fond/form/fields";
import { MapContext } from "fond/map/MapProvider";
import { useCustomStyles } from "fond/styleEditor/FieldFactory";

import { AttributeField } from "./attributeHelpers";

import { StyledTable } from "./FeatureAttributesForm.styles";

export type { AttributeField };

interface FeatureAttributesFormProps {
  feature: Feature;
  attributes: AttributeField[];
  onSubmit: (values: Record<string, any>) => void;
  onCancel?: () => void;
  submitLabel?: string;
  cancelLabel?: string;
  showReset?: boolean;
  disableOnPristine?: boolean;
}

interface FormValues {
  [key: string]: string | number | boolean;
}

/**
 * Common component for displaying and editing feature attributes in a table format
 */
const FeatureAttributesForm: React.FC<FeatureAttributesFormProps> = ({
  feature,
  attributes,
  onSubmit,
  onCancel,
  submitLabel = "Save",
  cancelLabel = "Cancel",
  showReset = false,
  disableOnPristine = false,
}) => {
  const { drawControl } = useContext(MapContext);
  const fieldClasses = useCustomStyles();

  const handleFormSubmit = useCallback(
    (values: FormValues) => {
      if (!drawControl.current || !feature.id) return;

      // Update feature properties with attribute values
      // setFeatureProperty updates the feature in place without firing draw.update
      Object.entries(values).forEach(([attributeName, value]) => {
        drawControl.current?.setFeatureProperty(feature.id as string, attributeName, value);
      });

      onSubmit(values);
    },
    [drawControl, feature.id, onSubmit]
  );

  // Build initial values from existing feature properties
  const initialValues: FormValues = {};
  attributes.forEach((attr) => {
    const existingValue = feature.properties?.[attr.ID];
    if (attr.Type === "BOOLEAN") {
      initialValues[attr.ID] = existingValue ?? false;
    } else {
      initialValues[attr.ID] = existingValue ?? "";
    }
  });

  // Render table row for each attribute with name in left column and field in right column
  const renderAttributeRow = useCallback(
    (attribute: AttributeField) => {
      let fieldComponent: React.ReactNode;

      // Handle BOOLEAN type with SwitchField
      if (attribute.Type === "BOOLEAN") {
        fieldComponent = <SwitchField name={attribute.ID} label="" />;
      } else if (attribute.Type === "ENUM" && attribute.Options) {
        // Handle ENUM type with SelectField
        const options = attribute.Options.map((option) => ({
          value: option,
          displayValue: option,
        }));
        fieldComponent = <SelectField name={attribute.ID} options={options} fullWidth size="small" className={fieldClasses.selectField} />;
      } else if (attribute.Type === "INTEGER" || attribute.Type === "REAL") {
        // Handle INTEGER and REAL types with TextField
        const step = attribute.Type === "REAL" ? "0.01" : "1";

        fieldComponent = (
          <TextField
            name={attribute.ID}
            type="number"
            fieldProps={{ InputProps: { inputProps: { step } } }}
            fullWidth
            className={fieldClasses.textField}
            classes={{ root: fieldClasses.textFieldRoot }}
            placeholder="NULL"
          />
        );
      } else {
        // Handle STRING type with TextField
        fieldComponent = (
          <TextField
            name={attribute.ID}
            type="text"
            fullWidth
            className={fieldClasses.textField}
            classes={{ root: fieldClasses.textFieldRoot }}
            placeholder="NULL"
          />
        );
      }

      return (
        <TableRow key={attribute.ID}>
          <TableCell sx={{ width: "40%", verticalAlign: "middle" }}>{attribute.ID}</TableCell>
          <TableCell sx={{ width: "60%" }}>{fieldComponent}</TableCell>
        </TableRow>
      );
    },
    [fieldClasses]
  );

  return (
    <Form<FormValues>
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      render={({ handleSubmit, form, submitting, pristine, dirty }) => (
        <Box component="form" onSubmit={handleSubmit} sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
            <StyledTable size="small" variant="ag-grid-compact">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2" color="textSecondary">
                      Attribute
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" color="textSecondary">
                      Value
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{attributes.map(renderAttributeRow)}</TableBody>
            </StyledTable>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 1, borderTop: 1, borderColor: "divider", mt: 2 }}>
            {showReset && (
              <Button onClick={() => form.reset()} disabled={submitting}>
                Reset
              </Button>
            )}
            {onCancel && (
              <Button onClick={onCancel} disabled={submitting}>
                {cancelLabel}
              </Button>
            )}
            <Button type="submit" variant="contained" color="primary" disabled={submitting || (disableOnPristine && !dirty)}>
              {submitLabel}
            </Button>
          </Box>
        </Box>
      )}
    />
  );
};

export default FeatureAttributesForm;
